import { ThemeBase } from './themeBase'

interface CreateMixinsResult {
    boxShadowSurfaceXs: () => string
    boxShadowSurfaceLarge: () => string
    fullContent: () => string
    customScrollbar: () => string
    mainHeading: () => string
    secondaryHeading: () => string
    heading4: () => string
    tableHeaderMain: () => string
    tableHeaderSecondary: () => string
    tableHeaderIncreased: () => string
    keyInsightsPanelIcon: () => string
    overviewHeadlineValue: () => string
    overviewTitle: () => string
    filtersDefaultButton: () => string
    filtersTextButton: () => string
    tableSlideOutDetailsButton: () => string
    roiOptimizationFormStyles: () => string
}

export const createMixins = <T extends ThemeBase>(
    theme: T
): CreateMixinsResult => ({
    boxShadowSurfaceXs: () => `
      box-shadow: 0 1px 1px -1px rgb(0, 0, 0, 0.1),
                  0 2px 1px -1px rgb(0, 0, 0, 0.02),
                  0 2px 2px -2px rgb(0, 0, 0, 0.04);
    `,
    boxShadowSurfaceLarge: () => `
      box-shadow: 0 6px 16px 0 rgb(0, 0, 0, 0.08),
                  0 3px 6px -4px rgb(0, 0, 0, 0.12),
                  0 9px 28px 8px rgb(0, 0, 0, 0.05);
    `,
    fullContent: () => `
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 999;
    `,
    customScrollbar: () => `
      &::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 8px;
        height: 8px;
        position: absolute;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 8px;
        background-color: rgb(233, 235, 238);
        box-shadow: 0 0 1px rgb(255, 255, 255, 0.1);
      }
    `,
    mainHeading: () => `
      ${theme.textStyles.headingMd};
      color: ${theme.colors.textMainHeading};
      margin: 0;
    `,
    secondaryHeading: () => `
      ${theme.textStyles.headingSm};
      color: ${theme.colors.textPrimary};
      margin: 0;
    `,
    heading4: () => `
      ${theme.textStyles.headingSm};
      color: ${theme.colors.textPrimary};
      margin: 0;
    `,
    tableHeaderMain: () => `
      ${theme.textStyles.headingSm};
      color: ${theme.colors.textBase};
    `,
    tableHeaderSecondary: () => `
      ${theme.textStyles.headingXs};
      color: ${theme.colors.textLighter};
    `,
    tableHeaderIncreased: () => `
      ${theme.textStyles.headingXs};
      color: ${theme.colors.textBase};
    `,
    keyInsightsPanelIcon: () => `
      display: flex;
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      justify-content: center;
      align-items: center;
      border-radius: 6px;
      font-size: 16px;
    `,
    overviewHeadlineValue: () => `
      font-size: 23px;
      letter-spacing: -0.03em;
      font-weight: ${theme.fontWeight.regular};
      color: ${theme.colors.textBase};
    `,
    overviewTitle: () => `
      ${theme.textStyles.bodyMain};
      color: ${theme.colors.textMuted};
      line-height: 1;
    `,
    filtersDefaultButton: () => `
      display: flex;
      align-items: center;
      height: ${theme.filters.defaultHeight};
      font-size: ${theme.fontSize.bodyMd};
      font-weight: ${theme.fontWeight.semiBold};
      border-radius: ${theme.radius.main};
      padding: 0 10px;
      cursor: pointer;
      .ant-btn-icon { font-size: 12px; }
      &:hover {
        background-color: #F6F6F6;
        color: ${theme.colors.textBase} !important;
        border-color: ${theme.colors.blackAlpha15} !important;
      }
    `,
    filtersTextButton: () => `
      display: flex;
      align-items: center;
      height: ${theme.filters.defaultHeight};
      font-size: ${theme.fontSize.bodyMd};
      font-weight: ${theme.fontWeight.semiBold};
      border-radius: ${theme.radius.main};
      color: ${theme.colors.textLight};
      padding: 0 8px;
      cursor: pointer;
      border: none;
      line-height: 1.5;
      .ant-btn-icon { font-size: 12px; }
      &:hover { border-color: transparent !important; }
    `,
    tableSlideOutDetailsButton: () => `
      .ant-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 28px;
        padding: 4px 7px;
        font-size: ${theme.fontSize.bodyMd};
        font-weight: ${theme.fontWeight.semiBold};
        border: 1px solid ${theme.colors.colorBorderMain};
        background-color: ${theme.colors.white};
        box-shadow: 0 2px 2px -1px rgb(0,0,0,0.07);
      }
      .ant-btn:hover {
        color: ${theme.colors.textPrimary};
        border-color: ${theme.colors.blackAlpha25};
        box-shadow: 0 3px 3px -1px ${theme.colors.blackAlpha10};
        background-color: ${theme.colors.white};
      }
      .ant-btn:hover .ant-btn-icon svg { fill: ${theme.colors.textPrimary}; }
      .ant-btn .ant-btn-icon { margin-inline-end: 5px; }
    `,
    roiOptimizationFormStyles: () => `
      .roi-optimization-form-row {
        display: flex;
        align-items: center;
      }
      .roi-optimization-form-row .roi-optimization-input-wrap {
        width: 50%;
        max-width: 350px;
      }
      .roi-optimization-form-row .roi-optimization-input-wrap > div {
        margin: 0 0 8px;
      }
      .roi-form-separator {
        display: flex;
        width: 28px;
        align-self: flex-start;
        justify-content: center;
        padding-top: 29px;
      }
    `,
})
